import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  FormControl,
  Typography,
  Select,
  MenuItem,
  Button,
  Paper,
  FormHelperText,
  TextField,
} from "@mui/material";
import SearchFilter from "../../../Requirement/components/common/SearchFilter";
import DataTable from "../../../Requirement/components/common/ReusableTable";
import {
  fetchData,
  searchfetchQueries,
} from "../../../Requirement/services/RequirementServices";
import { v4 as uuidv4 } from "uuid";

const TradeComodity = () => {
  const navigate = useNavigate();
  const [activeUser, setActiveUser] = useState("");
  const [formData, setFormData] = useState({
    productName: "",
    grade: "",
    type: "sell",
  });
  const [errors, setErrors] = useState({});
  const [searchValues, setSearchValues] = useState({
    searchCategory: "",
    searchProduct: "",
    searchGrade: "",
    searchPriceRange: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rowStart, setRowStart] = useState(0);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [productCategoryList, setProductCategoryList] = useState([]);
  const [gradeList, setGradeList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [pageSize, setPageSize] = useState(8);
  const [totalItems, setTotalItems] = useState(0);
  const [products, setProducts] = useState([]);

  // Options
  const typeOptions = [
    { value: "", label: "Select Type" },
    { value: "buy", label: "Buy" },
    { value: "sell", label: "Sell" },
  ];

  // Validation
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.productName) {
      newErrors.productName = "Please select a product name";
      isValid = false;
    }
    if (!formData.grade) {
      newErrors.grade = "Please select a grade";
      isValid = false;
    }
    if (!formData.type) {
      newErrors.type = "Please select a type";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    // Filter grade list when product is changed
    if (name === "productName" && productList.length > 0) {
      const filteredGrades = productList
        .filter((product) => product.ProductName === value)
        .map((product) => product.Grade)
        .filter(Boolean) // Remove empty grades
        .filter((grade, index, arr) => arr.indexOf(grade) === index) // Remove duplicates
        .map((grade) => ({ value: grade, label: grade }));

      setGradeList(filteredGrades);

      // Clear the grade selection if it's not available for the new product
      if (
        formData.grade &&
        !filteredGrades.some((g) => g.value === formData.grade)
      ) {
        setFormData((prev) => ({ ...prev, grade: "" }));
      }
    }
  };

  // Load product categories (run once)
  const loadProductCategoryList = async () => {
    try {
      setLoading(true);
      const initialResponse = await fetchData({
        target: "listProductCategory",
        userOrder: "CategoryName",
        rowStart: 0,
        pageSize: 100,
      });
      const totalCount = initialResponse?.total || initialResponse?.length || 0;
      if (totalCount > 0) {
        const allItems = await searchfetchQueries({
          target: "listProductCategory",
          userOrder: "CategoryName",
          rowStart: 0,
          pageSize: totalCount,
        });
        setProductCategoryList(allItems.items || allItems);
      }
    } catch (error) {
      console.error("Error loading product categories: ", error);
      setProductCategoryList([]);
    } finally {
      setLoading(false);
    }
  };

  // Load grades (run once)
  const loadGradeList = async () => {
    try {
      setLoading(true);
      const response = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 100,
      });
      const products = response.items || response;
      const uniqueGrades = [
        ...new Set(products.map((product) => product.Grade).filter(Boolean)),
      ].map((grade) => ({ value: grade, label: grade }));
      setGradeList(uniqueGrades);
    } catch (error) {
      console.error("Error loading grades: ", error);
    } finally {
      setLoading(false);
    }
  };

  const loadProductList = async () => {
    try {
      setLoading(true);
      const initialResponse = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 100,
      });
      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const allItems = await searchfetchQueries({
          target: "listProducts",
          userOrder: "ProductName",
          rowStart: 0,
          pageSize: totalCount,
        });
        setProductList(allItems.items || allItems);
      }
    } catch (error) {
      console.error("Error loading product list: ", error);
    } finally {
      setLoading(false);
    }
  };

  // Load table data (depends on category + pageSize)
  const loadTableData = useCallback(
    async (rowStart = 0, filters = {}) => {
      try {
        setLoading(true);
        const response = await searchfetchQueries({
          target: "listUserProductsVw",
          userOrder: "CreatedAt",
          rowStart,
          pageSize,
          filterTerms: filters,
        });
        const items = response.items || response;
        const modifiedItems = (response.items || response).map((item) => ({
          ...item,
          id: item.AssociateID, // Add id property with value from AssociateID
        }));
        setProducts(modifiedItems);
        setTotalItems(response.total || items.length);
      } catch (error) {
        console.error("Error loading table data: ", error);
        setProducts([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize]
  );

  // Search products
  const searchProduct = useCallback(
    async (filters, newRowStart = rowStart) => {
      const filterTerms = {};
      if (filters.searchCategory) {
        filterTerms.ProductCategoryID = filters.searchCategory;
      }
      if (filters.searchProduct) {
        filterTerms.ProductName = filters.searchProduct;
      }
      if (filters.searchGrade) {
        filterTerms.Grade = filters.searchGrade;
      }
      if (filters.searchPriceRange) {
        filterTerms.Type = filters.searchPriceRange;
      }
      await loadTableData(newRowStart, filterTerms);
    },
    [loadTableData, rowStart]
  );

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      // Find the ProductID corresponding to the selected ProductName
      const selectedProduct = productList.find(
        (product) => product.ProductName === formData.productName
      );
      const productId = selectedProduct ? selectedProduct.ProductID : null;

      if (!productId && !isEditMode) {
        setErrors((prev) => ({
          ...prev,
          productName: "Invalid product selected",
        }));
        setLoading(false);
        return;
      }

      const payload = {
        target: "createUserProductAssociation",
        userProductAssociationInput: {
          ProductID: productId,
          AssociateID: uuidv4(),
          EmailID: activeUser,
          Type: formData.type,
        },
      };

      await fetchData(payload);

      setSubmitted(true);
      setIsFormVisible(false);
      setFormData({
        productName: "",
        grade: "",
        type: "sell",
      });
      setErrors({});
      await loadTableData(rowStart);
    } catch (error) {
      console.error("Error submitting form: ", error);
      setErrors((prev) => ({
        ...prev,
        general: "Failed to submit form. Please try again.",
      }));
    } finally {
      setLoading(false);
    }
  };

  // Auth check (run once)
  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        if (
          !decodedToken["cognito:groups"]?.includes("AMTPL_GLOBAL_PARTNERS")
        ) {
          await Auth.signOut();
          navigate("/login/global-partner");
        } else {
          setActiveUser(user.attributes.email);
        }
      } catch {
        await Auth.signOut();
        navigate("/login/global-partner");
      }
    };
    checkUser();
  }, [navigate]);

  // Load static lists once
  useEffect(() => {
    loadProductCategoryList();
    loadGradeList();
    loadProductList();
  }, []);

  // Load table whenever category list/page size changes
  useEffect(() => {
    loadTableData(0);
  }, [loadTableData]);

  // Handlers
  const handleAdd = () => {
    if (isFormVisible) {
      setIsFormVisible(false);
      setErrors({});
      setSelectedRow(null);
    } else {
      setFormData({
        category: "",
        productName: "",
        grade: "",
        type: "sell",
      });
      setErrors({});
      setIsEditMode(true); // Set to true for adding new items
      setSelectedRow(null); // Clear selected row
      setIsFormVisible(true);

      // Reset grade list to show all available grades for new entries
      if (productList.length > 0) {
        const allGrades = [
          ...new Set(
            productList.map((product) => product.Grade).filter(Boolean)
          ),
        ].map((grade) => ({ value: grade, label: grade }));
        setGradeList(allGrades);
      }
    }
  };

  const handleRowClick = (row) => {
    console.log("Row clicked:", row); // Debug log to see row structure
    setIsEditMode(false); // Set to false since we're just viewing details
    setIsFormVisible(true);
    setSelectedRow(row);

    // Map the row data to form data with proper field names for display
    const selectedProductName = row.ProductName || row.productName || "";
    const selectedGrade = row.Grade || row.grade || "";
    const selectedType = row.Type || row.type || "";
    const selectedCategory = row.CategoryName || row.categoryName || "";

    setFormData({
      category: selectedCategory,
      productName: selectedProductName,
      grade: selectedGrade,
      type: selectedType,
    });

    setErrors({});
  };

  const handleSearch = () => {
    setRowStart(0);
    searchProduct(searchValues, 0);
  };

  // Search fields
  const fields = [
    {
      name: "searchCategory",
      label: "Category",
      type: "dropdown",
      options: [
        { value: "", label: "Select Category" },
        ...productCategoryList.map((item) => ({
          value: item.ProductCategoryID,
          label: item.CategoryName,
        })),
      ],
      value: searchValues.searchCategory,
      onChange: (value) => {
        setSearchValues((prev) => ({
          ...prev,
          searchCategory: value,
          searchProduct: "",
          searchGrade: "",
        }));
        searchProduct(
          {
            ...searchValues,
            searchCategory: value,
            searchProduct: "",
            searchGrade: "",
          },
          0
        );
      },
    },
    {
      name: "searchProduct",
      label: "Product",
      type: "dropdown",
      options: [
        { value: "", label: "Select Product" },
        ...[
          ...new Set(
            productList
              .filter(
                (product) =>
                  !searchValues.searchCategory ||
                  product.ProductCategoryID === searchValues.searchCategory
              )
              .map((product) => product.ProductName)
          ),
        ].map((product) => ({ value: product, label: product })),
      ],
      value: searchValues.searchProduct,
      onChange: (value) => {
        setSearchValues((prev) => ({
          ...prev,
          searchProduct: value,
          searchGrade: "",
        }));
        searchProduct(
          { ...searchValues, searchProduct: value, searchGrade: "" },
          0
        );
      },
    },
    {
      name: "type",
      label: "Type",
      type: "dropdown",
      options: typeOptions,
      value: searchValues.searchPriceRange,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchPriceRange: value }));
        searchProduct({ ...searchValues, searchPriceRange: value }, 0);
      },
    },
  ];

  const columns = [
    { id: "CategoryName", numeric: false, label: "CATEGORY" },
    { id: "ProductName", numeric: false, label: "PRODUCT NAME" },
    { id: "Grade", numeric: false, label: "GRADE" },
    {
      id: "Type",
      numeric: false,
      label: "Status",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";
        switch (row.Type) {
          case "sell":
            bgColor = "#f0a6898e";
            textColor = "#b41209ff";
            break;
          case "buy":
            bgColor = "#98e09396";
            textColor = "#04932aff";
            break;
          default:
            bgColor = "#f3f4f6";
            textColor = "#374151";
        }
        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {row.Type}
          </span>
        );
      },
    },
  ];

  return (
    <div className="container">
      <div className="table-header-re">
        <div className="head-btn">
          <button className="submit-btn-more" onClick={handleAdd}>
            {isFormVisible ? "Cancel" : "Add Trade"}
          </button>
        </div>
      </div>
      <Paper elevation={2} sx={{ p: 3 }}>
        <div className="search-filter4">
          <div className="title4">
            <div className="head-text">Trade Listings</div>
          </div>
          <div className="filter4">
            <SearchFilter fields={fields} onSearch={handleSearch} />
          </div>
        </div>
        <div className="table-content">
          <DataTable
            columns={columns}
            data={products}
            onRowClick={handleRowClick}
            totalItems={totalItems}
            pageSize={pageSize}
            rowStart={rowStart}
            setRowStart={setRowStart}
            setPageSize={setPageSize}
          />
        </div>

        {isFormVisible && (
          <div className="raise-ticket">
            <div className="table-header">
              <div className="header-text">
                <div className="head-text">
                  {isEditMode
                    ? "Add Trade"
                    : selectedRow
                    ? "Trade Details"
                    : "Add Trade"}
                </div>
              </div>
              {isEditMode && (
                <div className="head-btn">
                  <div className="submit-btn-form">
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      form="ticketForm"
                      disabled={loading}
                    >
                      {loading
                        ? "Submitting..."
                        : submitted
                        ? "Submitted"
                        : "Submit"}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <form id="ticketForm" onSubmit={handleSubmit} className="p-2">
              <div className="row gx-3 mb-3">
                {!isEditMode && selectedRow ? (
                  // Read-only view for selected row details
                  <>
                    <FormControl className="col-md-6 mb-3 p-3" sx={{ pl: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        Category
                      </Typography>
                      <TextField
                        value={formData.category || ""}
                        variant="outlined"
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{ backgroundColor: "#f5f5f5" }}
                      />
                    </FormControl>

                    <FormControl className="col-md-6 mb-3 p-3" sx={{ pr: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        Product Name
                      </Typography>
                      <TextField
                        value={formData.productName}
                        variant="outlined"
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{ backgroundColor: "#f5f5f5" }}
                      />
                    </FormControl>

                    <FormControl className="col-md-6 mb-3 p-3" sx={{ pl: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        Grade
                      </Typography>
                      <TextField
                        value={formData.grade}
                        variant="outlined"
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{ backgroundColor: "#f5f5f5" }}
                      />
                    </FormControl>

                    <FormControl className="col-md-6 mb-3 p-3" sx={{ pr: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        Type
                      </Typography>
                      <TextField
                        value={
                          formData.type === "buy"
                            ? "Buy"
                            : formData.type === "sell"
                            ? "Sell"
                            : formData.type
                        }
                        variant="outlined"
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{ backgroundColor: "#f5f5f5" }}
                      />
                    </FormControl>
                  </>
                ) : (
                  // Editable form for adding new items
                  <>
                    <FormControl
                      className="col-md-6 mb-3 p-3"
                      sx={{ pl: 1 }}
                      error={!!errors.productName}
                    >
                      <Typography variant="body2" gutterBottom>
                        Product Name
                      </Typography>
                      <Select
                        name="productName"
                        value={formData.productName}
                        onChange={handleChange}
                        displayEmpty
                        sx={{ backgroundColor: "white" }}
                      >
                        <MenuItem value="" disabled>
                          Select a product...
                        </MenuItem>
                        {[
                          ...new Set(
                            productList
                              .filter(
                                (product) =>
                                  !formData.category ||
                                  product.ProductCategoryID ===
                                    productCategoryList.find(
                                      (cat) =>
                                        cat.CategoryName === formData.category
                                    )?.ProductCategoryID
                              )
                              .map((product) => product.ProductName)
                          ),
                        ].map((productName) => (
                          <MenuItem key={productName} value={productName}>
                            {productName}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.productName && (
                        <FormHelperText>{errors.productName}</FormHelperText>
                      )}
                    </FormControl>

                    <FormControl
                      className="col-md-6 mb-3 p-3"
                      sx={{ pr: 1 }}
                      error={!!errors.grade}
                    >
                      <Typography variant="body2" gutterBottom>
                        Grade
                      </Typography>
                      <Select
                        name="grade"
                        value={formData.grade}
                        onChange={handleChange}
                        displayEmpty
                        sx={{ backgroundColor: "white" }}
                      >
                        <MenuItem value="" disabled>
                          Select a grade...
                        </MenuItem>
                        {gradeList.map((grade) => (
                          <MenuItem key={grade.value} value={grade.value}>
                            {grade.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.grade && (
                        <FormHelperText>{errors.grade}</FormHelperText>
                      )}
                    </FormControl>

                    <FormControl
                      className="col-md-6 mb-3 p-3"
                      sx={{ pr: 1 }}
                      error={!!errors.type}
                    >
                      <Typography variant="body2" gutterBottom>
                        Type
                      </Typography>
                      <Select
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                        displayEmpty
                        sx={{ backgroundColor: "white" }}
                      >
                        <MenuItem value="" disabled>
                          Select a type...
                        </MenuItem>
                        <MenuItem value="buy">Buy</MenuItem>
                        <MenuItem value="sell">Sell</MenuItem>
                      </Select>
                      {errors.type && (
                        <FormHelperText>{errors.type}</FormHelperText>
                      )}
                    </FormControl>
                  </>
                )}
              </div>
            </form>
          </div>
        )}
      </Paper>
    </div>
  );
};

export default React.memo(TradeComodity);
